// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Truncate from "@bg/App/Components/Truncate";
import { minicartStyles } from "@bg/App/Components/XngMiniCart/XngMiniCartStyles";
import LazyImage, { LazyImageProps } from "@bg/MobiusOverrides/LazyImage";
import { maxLengthAttr, maxQtyValue } from "@bg/XDL/Utilities/inputValidation";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonIcon } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import TextField from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import parse from "html-react-parser";
import React from "react";
import { connect, HandleThunkActionCreator } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    pageType: getCurrentPage(state).type,
    showTaxAndShipping: getSettingsCollection(state).cartSettings.showTaxAndShipping,
    isAuth: getSession(state).isAuthenticated,
    customSettings: getSettingsCollection(state).customSettings as any,
});

interface MiniCartLinesProps {
    setShowSpinner: any;
    cartLine: CartLineModel;
    updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine: (id: string) => void;
    closeOnClick: () => void;
    cartType?: string;
    shouldDisableQtyInput?: boolean;
}

interface MiniCartLineImageProps {
    smallImagePath?: string;
    productUri?: string;
    altText?: string;
    closeOnClick?: any;
}

interface MiniCartLineDescriptionProps {
    productUri?: string;
    shortDescription?: string;
    isRestricted?: boolean;
    isActive?: boolean;
    erpNumber?: string;
    mfrNumber?: string;
    closeOnClick: () => void;
}

interface MiniCartLineLeftSideProps {
    setShowSpinner: any;
    cartLine: CartLineModel;
    updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    cartType?: string;
    shouldDisableQtyInput?: boolean;
    showTaxAndShipping: boolean;
    pageType: string;
    isAuth: boolean;
    customSettings: any;
    closeOnClick: () => void;
}

const MiniCartLineLeftSide: React.FC<MiniCartLineLeftSideProps> = ({
    cartLine,
    updateCartLine,
    cartType,
    setShowSpinner,
    shouldDisableQtyInput,
    showTaxAndShipping,
    pageType,
    isAuth,
    customSettings,
    closeOnClick,
}) => {
    // using .toLocaleString() will cause out-of-range issues for big vlaues
    const [stateValue, setValue] = React.useState(cartLine.qtyOrdered?.toString());
    const regexp = /^[0-9\b]+$/;
    const isQuoteRequired = cartLine.quoteRequired;

    const [showPricing, setShowPricing] = React.useState(false);

    React.useEffect(() => {
        if (
            customSettings &&
            customSettings.postalCodeLocationFinder.toLowerCase() === "true" &&
            !window.localStorage.getItem("postalCodeID")?.length &&
            !isAuth
        ) {
            setShowPricing(false);
        } else {
            setShowPricing(true);
        }
    }, []);

    const isValidNumberic = (number: string) => {
        if (number === "") {
            // backspace or delete case
            return true;
        }
        return regexp.test(number);
    };

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.keyCode === 13) {
            event.stopPropagation();
            if (
                event.currentTarget.value !== "" &&
                isValidNumberic(event.currentTarget.value) &&
                parseInt(event.currentTarget.value, 10) <= maxQtyValue
            ) {
                (event.target as any).blur();
            }
        }
    };

    React.useEffect(() => {
        setValue(cartLine.qtyOrdered?.toString());
    }, [cartLine.qtyOrdered]);

    const qtyChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!isValidNumberic(event.currentTarget.value) || parseInt(event.currentTarget.value, 10) > maxQtyValue) {
            event.preventDefault();
            return;
        }
        const value = event.target.value;
        setValue(value);
    };

    const onBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        const stringValue = event.target.value;
        const value = Number.parseInt(stringValue, 10);

        if (!Number.isNaN(value) && value > 0 && value <= maxQtyValue && value !== cartLine.qtyOrdered) {
            setShowSpinner(true);
            updateCartLineValue(value);
        }
        if (value <= 0 || !value || value > maxQtyValue) {
            setValue(cartLine.qtyOrdered?.toString());
        }
    };

    const updateCartLineValue = (value: number) => {
        updateCartLine({
            cartLine: {
                ...cartLine,
                qtyOrdered: value,
            },
            showTaxAndShipping: pageType.toLowerCase() === "cartpage" && showTaxAndShipping,
            onSuccess: () => {
                setShowSpinner(false);
                if (stateValue !== cartLine.qtyOrdered?.toString()) {
                    setValue(cartLine.qtyOrdered?.toString());
                }
            },
        } as any);
    };

    const decreaseByOne = () => {
        const value = Number.parseInt(stateValue || "", 10);
        if (
            cartLine.properties["roundingRule"].toLowerCase() === "multiple only" &&
            parseInt(cartLine.properties["multipleSaleQty"], 10)
        ) {
            const step = parseInt(cartLine.properties["multipleSaleQty"], 10);
            updateCartLineValue(value - step);
            return;
        }
        updateCartLineValue(value - 1);
    };

    const increaseByOne = () => {
        const value = Number.parseInt(stateValue || "", 10);
        if (
            cartLine.properties["roundingRule"].toLowerCase() === "multiple only" &&
            parseInt(cartLine.properties["multipleSaleQty"], 10)
        ) {
            const step = parseInt(cartLine.properties["multipleSaleQty"], 10);
            updateCartLineValue(value + step);
            return;
        }
        updateCartLineValue(value + 1);
    };

    const getTotalExtendedActualPrice = () => {
        if (!showPricing || cartLine.properties.pricingFailed?.toLowerCase() === "true") {
            return translate("N/A");
        }
        if (isQuoteRequired) {
            return translate("Quote Required");
        }
        return cartLine?.pricing?.extendedActualPriceDisplay;
    };

    const getActualPrice = React.useCallback(() => {
        if (!showPricing || cartLine.properties.pricingFailed?.toLowerCase() === "true") {
            return translate("N/A");
        }
        if (isQuoteRequired) {
            return "";
        }
        return `${cartLine?.pricing?.actualPriceDisplay} / ${cartLine?.unitOfMeasure}`;
    }, [showPricing, isQuoteRequired, cartLine.properties.pricingFailed]);

    return (
        <StyledWrapper
            css={css`
                display: flex;
                flex-wrap: wrap;
                width: 100%;
            `}
        >
            <MiniCartLineDescription
                shortDescription={cartLine.shortDescription}
                productUri={cartLine.productUri}
                closeOnClick={closeOnClick}
                isActive={cartLine.isActive}
                isRestricted={cartLine.isRestricted}
                erpNumber={cartLine.properties.sku || cartLine.erpNumber}
                mfrNumber={cartLine.manufacturerItem}
            />
            <StyledWrapper
                css={css`
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    width: 100%;
                `}
            >
                <StyledWrapper
                    css={css`
                        width: 95px;
                        padding-right: 5px;
                    `}
                >
                    <Typography as="span" size="15px" lineHeight="20px" className="price">
                        {getActualPrice()}
                    </Typography>
                </StyledWrapper>

                <StyledWrapper
                    css={css`
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        flex-grow: 1;
                    `}
                >
                    <StyledWrapper {...minicartStyles.cartLineOrderedQtyWrapper} className="qty">
                        <Button onClick={decreaseByOne} sizeVariant="small" disabled={Number(stateValue) === 1}>
                            <ButtonIcon src="Remove" size={10} color="secondary.main" />
                        </Button>
                        <TextField
                            value={stateValue}
                            type="text"
                            label={translate("")}
                            data-test-selector="mini_cart_qtyOrdered"
                            disabled={
                                cartLine.isPromotionItem ||
                                cartType === "Job" ||
                                !cartLine.isActive ||
                                cartLine.isRestricted ||
                                shouldDisableQtyInput
                            }
                            maxLength={maxLengthAttr.qty}
                            onKeyPress={keyPressHandler}
                            onChange={qtyChangeHandler}
                            onBlur={onBlurHandler}
                        />
                        <Button onClick={increaseByOne} sizeVariant="small" disabled={Number(stateValue) === maxQtyValue}>
                            <ButtonIcon src="Plus" size={10} color="secondary.main" />
                        </Button>
                    </StyledWrapper>

                    <StyledWrapper
                        css={css`
                            width: calc(100% - 112px);
                            padding-left: 5px;
                            text-align: right;
                        `}
                    >
                        <Typography as="span" size="15px" lineHeight="20px" weight="bold" className="price">
                            {getTotalExtendedActualPrice()}
                        </Typography>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const MiniCartLineImage = ({ smallImagePath, productUri, altText, closeOnClick }: MiniCartLineImageProps) => {
    return (
        <Clickable href={productUri} {...minicartStyles.cartLineImageStyles} onClick={closeOnClick}>
            <LazyImage src={smallImagePath} altText={altText} cloudinary={true} cloudinaryWidth="114"></LazyImage>
        </Clickable>
    );
};

const lineNumber = `white-space: nowrap;
width: 50%;
overflow: hidden;
height: 22px;
text-overflow: ellipsis;`;

const MiniCartLineDescription = ({
    productUri,
    shortDescription,
    isActive,
    isRestricted,
    erpNumber,
    mfrNumber,
    closeOnClick,
}: MiniCartLineDescriptionProps) => {
    const description = shortDescription || "";
    return (
        <>
            {isActive && !isRestricted ? (
                <Clickable {...minicartStyles.cartLineDescriptionStyles} onClick={closeOnClick} href={productUri}>
                    <Typography color="secondary.main" size="18px" lineHeight="22px">
                        <Truncate lines={2} lineHeight={22}>
                            {parse(description)}
                        </Truncate>
                    </Typography>
                </Clickable>
            ) : (
                <StyledWrapper
                    css={css`
                        padding-right: 15px;
                        margin-bottom: 10px;
                        width: 100%:
                        `}
                >
                    <Typography color="secondary.main" size="18px" lineHeight="22px">
                        <Truncate lines={2} lineHeight={22}>
                            {parse(description)}
                        </Truncate>
                    </Typography>
                </StyledWrapper>
            )}
            <StyledWrapper
                css={css`
                    display: flex;
                    justify-content: space-between;
                    width: 100%;
                    margin-bottom: 7px;
                `}
            >
                {mfrNumber && (
                    <StyledWrapper
                        css={css`
                            ${lineNumber}
                            margin-right: 2px;
                        `}
                    >
                        <Typography as="strong" size="13px" lineHeight="20px">
                            {translate("MFR Part #:")}
                        </Typography>
                        <Typography size="13px" lineHeight="20px">
                            {mfrNumber}
                        </Typography>
                    </StyledWrapper>
                )}
                {erpNumber && (
                    <StyledWrapper
                        css={css`
                            ${lineNumber}
                            margin-right: 1px;
                        `}
                    >
                        <Typography as="strong" size="13px" lineHeight="20px">
                            {translate("SKU:")}
                        </Typography>
                        <Typography size="13px" lineHeight="20px">
                            {erpNumber}
                        </Typography>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </>
    );
};

const XngMiniCartLine: React.FC<MiniCartLinesProps & ReturnType<typeof mapStateToProps>> = ({
    cartLine,
    removeCartLine,
    updateCartLine,
    cartType,
    setShowSpinner,
    shouldDisableQtyInput,
    pageType,
    showTaxAndShipping,
    isAuth,
    customSettings,
    closeOnClick,
}) => {
    const isInactive = !cartLine.isActive;
    const isRestricted = cartLine.isRestricted;

    return (
        <StyledWrapper {...minicartStyles.cartLineStyles}>
            <Button
                className="remove"
                onClick={() => removeCartLine(cartLine.id)}
                {...minicartStyles.cartLineRemoveButtonStyles}
                aria-label="remove line"
            >
                <ButtonIcon color="secondary.main" src="Delete" size={15} />
            </Button>
            <StyledWrapper {...minicartStyles.cartLineRightSideStyles}>
                <MiniCartLineImage
                    smallImagePath={cartLine.smallImagePath}
                    altText={cartLine.altText}
                    productUri={cartLine.productUri}
                    closeOnClick={closeOnClick}
                />
            </StyledWrapper>
            <StyledWrapper {...minicartStyles.cartLineLeftSideStyles}>
                <MiniCartLineLeftSide
                    showTaxAndShipping={showTaxAndShipping}
                    pageType={pageType}
                    setShowSpinner={setShowSpinner}
                    cartLine={cartLine}
                    updateCartLine={updateCartLine}
                    cartType={cartType}
                    shouldDisableQtyInput={shouldDisableQtyInput}
                    isAuth={isAuth}
                    customSettings={customSettings}
                    closeOnClick={closeOnClick}
                />
                {isInactive && (
                    <StyledWrapper className="error-message">
                        <Typography as="span" color="danger.main">
                            {siteMessage("Custom_Mini_Cart_InactiveProduct_Msg")}
                        </Typography>
                    </StyledWrapper>
                )}
                {isRestricted && (
                    <StyledWrapper className="error-message">
                        <Typography as="span" color="danger.main">
                            {siteMessage("Custom_Mini_Cart_RestrictedProduct_Msg")}
                        </Typography>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};
export default connect(mapStateToProps)(XngMiniCartLine);
